import { User } from '@prisma/client'
import { Form, Radio, Typography } from 'antd'
import React from 'react'
import { CommonClient } from '~/agents/common/client'
import { OnboardingValues } from '~/agents/customerSupport/common'
import { ProviderAccountAuthorization } from '~/features/formConfiguration/client/components/CustomInputs/ProviderAccountAuthorization'
import { SelectProviderAccount } from '~/features/formConfiguration/client/components/CustomInputs/SelectProviderAccount'
import { TitlePrimary } from '~/features/shared/components'
import { StepProps } from '../types'

type Props = {
  user: User
} & StepProps

export const StepEmailProviders: React.FC<Props> = ({ user, ...props }) => {
  const ensureValues = (values: OnboardingValues): OnboardingValues => {
    return {
      ...values,
      emailServices: {
        ...values.emailServices,
        isActive: props.values.emailServices.isActive,
      },
    }
  }

  return (
    <>
      <CommonClient.Onboarding.Step
        {...props}
        onChange={values => props.onChange(ensureValues(values))}
        onBack={values => props.onBack(ensureValues(values))}
        onNext={values => props.onNext(ensureValues(values))}
      >
        <TitlePrimary level={4}>Choose an account</TitlePrimary>

        <Typography.Text type="secondary" className="mb-10">
          Great choice
        </Typography.Text>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const isActive = instance.getFieldValue([
              'emailServices',
              'isActive',
            ])

            if (!isActive) {
              return <></>
            }

            return (
              <Form.Item
                name={['emailServices', 'provider']}
                rules={[
                  {
                    required: true,
                    message: 'Choose a provider to continue',
                  },
                ]}
              >
                <Radio.Group
                  block={true}
                  optionType="button"
                  buttonStyle="solid"
                  options={[
                    {
                      label: 'Google',
                      value: 'gmail',
                    },
                    {
                      label: 'Outlook',
                      value: 'outlook',
                    },
                  ]}
                />
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {instance => {
            const isActive = instance.getFieldValue([
              'emailServices',
              'isActive',
            ])

            const provider = instance.getFieldValue([
              'emailServices',
              'provider',
            ])

            const providerAccountId = instance.getFieldValue([
              'emailServices',
              'gmailProviderAccountId',
            ])

            if (!isActive) {
              return <></>
            }

            if (provider === 'outlook') {
              return (
                <Form.Item
                  name={['emailServices', 'outlookProviderAccountId']}
                  rules={[
                    {
                      required: true,
                      message: 'Connect your account to continue',
                    },
                  ]}
                >
                  <SelectProviderAccount providerKey="outlookMail" />
                </Form.Item>
              )
            }

            return (
              <>
                <Form.Item
                  name={['emailServices', 'gmailProviderAccountId']}
                  rules={[
                    {
                      required: true,
                      message: 'Connect your account to continue',
                    },
                  ]}
                >
                  <SelectProviderAccount providerKey="gmail" />
                </Form.Item>

                {providerAccountId && (
                  <>
                    <Form.Item
                      name={['authorizations', 'gmailReadEmails']}
                      rules={[
                        {
                          validator: (_, value) =>
                            value?.[0]?.isCorrupted
                              ? Promise.reject()
                              : Promise.resolve(),
                          message: 'Authorization is not valid',
                        },
                        {
                          required: true,
                          message: 'You need to authorize to continue',
                        },
                      ]}
                    >
                      <ProviderAccountAuthorization
                        providerAccountId={providerAccountId}
                        scope="https://www.googleapis.com/auth/gmail.modify"
                        ui={{
                          title: 'Read Emails',
                          iconAntd: 'GoogleOutlined',
                          description: 'Allow Cara to read emails',
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      name={['authorizations', 'gmailDraftEmails']}
                      rules={[
                        {
                          validator: (_, value) =>
                            value?.[0]?.isCorrupted
                              ? Promise.reject()
                              : Promise.resolve(),
                          message: 'Authorization is not valid',
                        },
                        {
                          required: true,
                          message: 'You need to authorize to continue',
                        },
                      ]}
                    >
                      <ProviderAccountAuthorization
                        providerAccountId={providerAccountId}
                        scope="https://www.googleapis.com/auth/gmail.send"
                        ui={{
                          title: 'Send Emails',
                          iconAntd: 'GoogleOutlined',
                          description: 'Allow Cara to send emails',
                        }}
                      />
                    </Form.Item>
                  </>
                )}
              </>
            )
          }}
        </Form.Item>
      </CommonClient.Onboarding.Step>
    </>
  )
}
